import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const comic = useMemo(() => comics.find((c) => c.id === Number(id)), [id]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.category === comic.category && c.id !== comic.id)
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">漫画未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  return (
    <>
      <SEOHead
        title={`${comic.title}_${comic.author}_免费韩漫在线阅读-韩漫漫画网`}
        description={`韩漫漫画网提供${comic.title}免费在线阅读。${comic.description}作者${comic.author}，${comic.categoryName}题材，评分${comic.rating}，${formatViews(comic.views)}人气。`}
        keywords={`${comic.title},${comic.author},${comic.categoryName}韩漫,免费韩漫在线阅读,韩漫漫画网`}
        canonicalUrl={`https://www.hanmancomic.com/article/${comic.id}`}
        ogType="article"
        ogImage={comic.coverImage}
        pageType="article"
        articleData={{
          publishedTime: '2026-01-01',
          modifiedTime: comic.updateDate,
          authorName: comic.author,
        }}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: comic.title,
          description: comic.description,
          author: { '@type': 'Person', name: comic.author },
          datePublished: '2026-01-01',
          dateModified: comic.updateDate,
          image: comic.coverImage,
          url: `https://www.hanmancomic.com/article/${comic.id}`,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmancomic.com' },
            { '@type': 'ListItem', position: 2, name: comic.categoryName, item: `https://www.hanmancomic.com/category/${comic.category}` },
            { '@type': 'ListItem', position: 3, name: comic.title },
          ],
        }}
      />

      <Navbar />
      <Breadcrumb
        items={[
          { label: comic.categoryName, href: `/category/${comic.category}` },
          { label: comic.title },
        ]}
      />

      <main>
        {/* Comic Header */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            {/* Cover */}
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div className="w-48 md:w-56 rounded-lg overflow-hidden shadow-sm">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full aspect-[400/560] object-cover"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {comic.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {comic.author}
                </span>
                <span>·</span>
                <Link to={`/category/${comic.category}`} className="text-primary-600 hover:text-primary-700 font-medium">
                  {comic.categoryName}
                </Link>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i> {comic.rating}
                </span>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {formatViews(comic.views)} 人气
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
                <span className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                  共 {comic.chapters} 话
                </span>
                <span className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                  更新于 {comic.updateDate}
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-md text-xs bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {comic.description}
              </p>

              <Link
                to={`/article/${comic.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line"></i>
                开始阅读
              </Link>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-6">
          <div className="border-b border-background-200/70">
            <div className="flex gap-0">
              <button
                onClick={() => setActiveTab('info')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'info'
                    ? 'border-primary-500 text-primary-700'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                作品详情
              </button>
              <button
                onClick={() => setActiveTab('chapters')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'chapters'
                    ? 'border-primary-500 text-primary-700'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                章节列表
              </button>
            </div>
          </div>
        </section>

        {/* Tab Content */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-10">
          {activeTab === 'info' ? (
            <div className="max-w-3xl">
              <div
                className="text-sm text-foreground-700 leading-relaxed space-y-3 comic-description"
                dangerouslySetInnerHTML={{ __html: comic.fullDescription }}
              />
            </div>
          ) : (
            <div className="max-w-3xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {Array.from({ length: Math.min(comic.chapters, 30) }, (_, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 rounded-lg border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="text-sm text-foreground-800">第 {comic.chapters - i} 话</span>
                    <span className="text-xs text-foreground-400">
                      {new Date(new Date(comic.updateDate).getTime() - i * 7 * 24 * 60 * 60 * 1000)
                        .toISOString()
                        .slice(0, 10)}
                    </span>
                  </div>
                ))}
                {comic.chapters > 30 && (
                  <div className="flex items-center justify-center p-3 rounded-lg border border-dashed border-background-300/60 text-sm text-foreground-500 cursor-pointer hover:border-primary-300 col-span-full">
                    查看全部 {comic.chapters} 话
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="bg-background-100/50 py-10 md:py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} {...c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}